import React, { useEffect, useMemo, useRef } from "react";
import {
  Container,
  Box,
  Grid,
  Button,
  MenuItem,
  Typography,
  useMediaQuery,
  Stack,
  TextField,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Filters from "../filters";
import ProductListView from "../../../../Components/Widgets/ProductListView";
import ProductListSkeletonView from "../../widgets/product_list_skeleton_view";
import { resolvePageCopy } from "./collection-copy";

const CHIP_HIDDEN_KEYS = new Set(["gender", "sortBy"]);

const typeTabSx = (active) => ({
  fontSize: { xs: 12, md: 14 },
  fontFamily: "Spartan-SemiBold",
  letterSpacing: "0.04em",
  borderBottom: active ? "1px solid #000" : "1px solid transparent",
  p: 0,
  pb: 0.5,
  minWidth: 0,
  borderRadius: 0,
  whiteSpace: "nowrap",
  flexShrink: 0,
  color: "#000",
  "&:hover,&:focus": { backgroundColor: "transparent" },
});

function resolveFilterLabel(component, type, slug) {
  const items = component.getData(`default.${type}s`, []);
  const match = items.find(
    (item) => item?._values?.slug === slug || item?._id === slug,
  );
  if (match?._values?.name) {
    return match._values.name;
  }
  if (type === "price" && typeof slug === "string" && slug.includes(",")) {
    const [min, max] = slug.split(",");
    return `$${min} – $${max === "1000000" ? "∞" : max}`;
  }
  return slug;
}

export default function Collection(props) {
  const { component } = props;
  const theme = useTheme();
  const types = component.getData("default.types", []);
  const { products = null } = component.getData("default", {});
  const term = component.getSearchTerm();
  const devHelper = component.getHelpers("dev");
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const view = isDesktop ? "desktop" : "mobile";
  const [filterDrawer, setFilterDrawer] = React.useState(false);

  const pageGender = component.getGender();
  const pageType = component.getData("default.pageType", "");
  const pageValue = component.getData("default.pageValue", "");
  const page = `${pageGender}-${pageType}-${pageValue}`;
  const { title: titleText, subtitle: subtitleText } = resolvePageCopy({
    page,
    pageGender,
    pageValue,
    view,
  });
  const hasTitle = Boolean(titleText);
  const hasSubtitle = Boolean(subtitleText);

  const visibleTypes = useMemo(
    () => types.filter((type) => component.typeIsVisible(type)),
    [types, component],
  );
  const showTypes =
    component.showTypes() && visibleTypes.length > 0 && products?.length > 0;

  const activeFilters = component.getData("default.activeFilters", {});
  const sortValue = (activeFilters.sortBy || []).join("-") || "";
  const total = component.getData("default.total", 0);

  const activeChips = useMemo(() => {
    const chips = [];
    Object.entries(activeFilters).forEach(([type, values]) => {
      if (CHIP_HIDDEN_KEYS.has(type) || !values?.length) return;
      // Skip page-level primary filter (e.g. curation/collection from URL)
      if (type === pageType && values.length === 1 && values[0] === pageValue) {
        return;
      }
      values.forEach((slug) => {
        chips.push({
          key: `${type}-${slug}`,
          type,
          slug,
          label: resolveFilterLabel(component, type, slug),
        });
      });
    });
    return chips;
  }, [activeFilters, component, pageType, pageValue]);

  const isFetching = useRef(false);

  useEffect(() => {
    const onScroll = () => {
      const max = 0.4;

      if (
        !isFetching.current &&
        !component.getData("default.loading") &&
        component.canFetchMore() &&
        (window.scrollY + window.innerHeight) / document.body.clientHeight > max
      ) {
        isFetching.current = true;
        component.fetchMore();
        setTimeout(() => {
          isFetching.current = false;
        }, 1000);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, [component]);

  const selectAllTypes = () => {
    setFilterDrawer(false);
    component.allTypes();
  };

  const selectType = (slug) => {
    setFilterDrawer(false);
    component.selectType(slug);
  };

  const removeChip = (type, slug) => {
    const items = component.getData(`default.${type}s`, []);
    const item = items.find((entry) => entry?._values?.slug === slug) || {
      _values: { slug },
    };
    component.toggleFilter(type, item);
  };

  return (
    <Container
      disableGutters
      maxWidth="xl"
      sx={{
        mt: { xs: 2, md: 3 },
        overscrollBehavior: "none",
        touchAction: "pan-y",
        WebkitOverflowScrolling: "touch",
      }}
    >
      {(term || hasTitle || hasSubtitle) && (
        <Stack
          spacing={1}
          sx={{
            justifyContent: "center",
            alignItems: "center",
            px: 2,
            mb: { xs: 2, md: 3 },
          }}
        >
          {term ? (
            <Typography
              component="h1"
              align="center"
              sx={{ ...theme.custom.titles.title }}
            >
              Search Results For: «{decodeURIComponent(term)}»
            </Typography>
          ) : (
            hasTitle && (
              <Typography
                component="h1"
                align="center"
                sx={{ ...theme.custom.titles.title }}
              >
                {titleText}
              </Typography>
            )
          )}
          {!term && hasSubtitle && (
            <Typography
              component="p"
              align="center"
              sx={{
                ...theme.custom.titles.description,
                maxWidth: 640,
              }}
            >
              {subtitleText}
            </Typography>
          )}
        </Stack>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: { xs: "flex-start", md: "center" },
          overflowX: "auto",
          px: 2,
          mb: { xs: 2, md: 2.5 },
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
          height: { xs: 30, md: 34 },
        }}
      >
        {showTypes && (
          <Stack direction="row" spacing={{ xs: 2.5, md: 3 }} sx={{ pb: 0.5 }}>
            <Button
              sx={typeTabSx(
                !visibleTypes.some((t) => component.isOnType(t._values?.slug)),
              )}
              disableRipple
              onClick={selectAllTypes}
            >
              ALL
            </Button>
            {visibleTypes.map((type) => {
              const { slug, name } = type._values || {};
              return (
                <Button
                  key={`type-${slug}`}
                  sx={typeTabSx(component.isOnType(slug))}
                  disableRipple
                  onClick={() => selectType(slug)}
                >
                  {component.ucfirst(name)}
                </Button>
              );
            })}
          </Stack>
        )}
      </Box>

      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          bgcolor: "#fff",
          borderBottom: "1px solid rgba(0,0,0,0.08)",
          px: { xs: 1.5, md: 2 },
          py: 1.25,
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Button
            sx={{
              borderRadius: 0,
              fontSize: { xs: 13, md: 14 },
              fontFamily: "Spartan-Medium",
              textTransform: "none",
              p: 0,
              minWidth: 0,
              color: "#000",
              "&:hover": { background: "transparent" },
              "& i": { fontSize: 16, mr: 1 },
            }}
            onClick={() => setFilterDrawer((prev) => !prev)}
            disableRipple
          >
            <i className="fa-light fa-sliders" />
            Filters
            {activeChips.length > 0 && (
              <Box
                component="span"
                sx={{
                  ml: 0.75,
                  fontFamily: "Spartan-Bold",
                }}
              >
                ({activeChips.length})
              </Box>
            )}
          </Button>

          <Typography
            sx={{
              fontSize: { xs: 12, md: 13 },
              fontFamily: "Spartan-Medium",
              color: "#000",
              letterSpacing: "0.02em",
            }}
          >
            {`${total} products`}
          </Typography>

          <TextField
            select
            size="small"
            value={sortValue}
            onChange={(e) => {
              const value = e.target.value;
              if (!value) {
                const next = {
                  ...component.getData("default.activeFilters", {}),
                };
                delete next.sortBy;
                component.setData({ "default.activeFilters": next });
                component.fetchProducts();
                return;
              }
              component.sortBy(value);
            }}
            variant="standard"
            InputProps={{ disableUnderline: true }}
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected) => {
                if (!selected) return "Sort";
                if (selected === "price-asc") return "Price ↑";
                if (selected === "price-desc") return "Price ↓";
                return "Sort";
              },
            }}
            sx={{
              minWidth: 88,
              "& .MuiInputBase-input": {
                fontFamily: "Spartan-Medium",
                fontSize: { xs: 13, md: 14 },
                py: 0.5,
                textAlign: "right",
              },
              "& .MuiSelect-icon": {
                right: -2,
              },
            }}
          >
            <MenuItem value="">Featured</MenuItem>
            <MenuItem value="price-asc">Price: Low to High</MenuItem>
            <MenuItem value="price-desc">Price: High to Low</MenuItem>
          </TextField>
        </Stack>

        {activeChips.length > 0 && (
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            sx={{ mt: 1.25, gap: 1 }}
          >
            {activeChips.map((chip) => (
              <Chip
                key={chip.key}
                label={chip.label}
                onDelete={() => removeChip(chip.type, chip.slug)}
                size="small"
                sx={{
                  borderRadius: 0,
                  height: 28,
                  bgcolor: "#f4f4f1",
                  fontFamily: "Spartan-Medium",
                  fontSize: 11,
                  textTransform: "uppercase",
                  "& .MuiChip-deleteIcon": {
                    fontSize: 16,
                    color: "#000",
                    "&:hover": { color: "#000" },
                  },
                }}
              />
            ))}
            <Button
              disableRipple
              onClick={() => component.clearFiltersAndFetch()}
              sx={{
                p: 0,
                minWidth: 0,
                fontSize: 11,
                fontFamily: "Spartan-SemiBold",
                textTransform: "uppercase",
                color: "#000",
                borderBottom: "1px solid #000",
                borderRadius: 0,
                lineHeight: 1.2,
                "&:hover": { bgcolor: "transparent" },
              }}
            >
              Clear all
            </Button>
          </Stack>
        )}
      </Box>

      <Filters
        {...props}
        filterDrawer={filterDrawer}
        setFilterDrawer={setFilterDrawer}
      />

      <Box sx={{ position: "relative", minHeight: 600, mt: 1 }}>
        <Grid
          container
          sx={{ alignItems: "flex-start" }}
          direction="row"
          spacing={1}
        >
          {products === null ? (
            [...Array(24)].map((_, index) => (
              <ProductListSkeletonView
                key={`skeleton-${index}`}
                {...props}
                grid={4}
              />
            ))
          ) : products.length > 0 ? (
            products.map((product) => (
              <Grid
                item
                md={4}
                sm={6}
                xs={6}
                key={devHelper.getObjectValue(product, "_id")}
              >
                <ProductListView {...props} product={product} />
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Typography
                align="center"
                sx={{ my: 12, fontFamily: "Spartan-SemiBold", fontSize: 16 }}
              >
                NO PRODUCTS FOUND
              </Typography>
            </Grid>
          )}

          {products &&
            products.length !== total &&
            [...Array(isDesktop ? 3 : 2)].map((_, index) => (
              <ProductListSkeletonView
                key={`more-skeleton-${index}`}
                {...props}
                grid={4}
              />
            ))}
        </Grid>
      </Box>
    </Container>
  );
}
